import {
  BaseEditor,
  type EditorFeatureSet,
  type EditorProseSize,
} from './BaseEditor';
import { useMountNormalizationGuard } from './useMountNormalizationGuard';

// TrailingNode's always-present empty last paragraph is editor chrome, not content
const stripTrailingEmptyLine = (html: string) =>
  html.replace(/<p class="trailing-empty-line"><\/p>$/, '');

export interface HtmlEditorProps {
  id: string;
  /** Initial HTML content to display in the editor */
  value: string;
  /** Callback fired when editor content changes, receives an HTML string */
  onChange: (html: string) => void;
  ariaLabel?: string;
  placeholder?: string;
  /** Which editor feature set to expose. @default 'full' */
  featureSet?: EditorFeatureSet;
  /** Contenteditable typography density. @default 'base' */
  proseSize?: EditorProseSize;
  className?: string;
  /** When true, focus the editor on mount. @default false */
  autoFocus?: boolean;
  /** Extension names to exclude from the default set */
  excludeExtensions?: string[];
}

export const HtmlEditor = ({
  id,
  value,
  onChange,
  ariaLabel,
  placeholder,
  featureSet,
  proseSize,
  className,
  autoFocus = false,
  excludeExtensions,
}: HtmlEditorProps) => {
  const { onCreate, onUpdate } = useMountNormalizationGuard(
    (editor) => stripTrailingEmptyLine(editor.getHTML()),
    onChange
  );

  return (
    <BaseEditor<string>
      id={id}
      initialContent={value}
      onCreate={onCreate}
      onUpdate={onUpdate}
      ariaLabel={ariaLabel}
      placeholder={placeholder}
      featureSet={featureSet}
      proseSize={proseSize}
      className={className}
      autofocus={autoFocus}
      excludeExtensions={excludeExtensions}
    />
  );
};
